/*
  GSG Fast Order — trade ordering behaviour.
  Built 2026-08-23. Scoped to the .gsg-fastorder component; adds nothing global.

  The section renders a flat JSON catalogue into the page (inside the trade-only
  branch, so a guest never receives trade data at all). This script groups it by
  crop and drives two layouts from one source of truth: a dense table on desktop,
  ordering cards on mobile. Both write to the same row state, so rotating a phone
  mid-order keeps the basket intact.

  Deliberate choices:
  - No framework, no build step, no external library.
  - A row is "ready" when its quantity is 1 or more. That is the only selection
    model, so there is no separate checkbox to forget to tick.
  - Every cart write goes through one guard flag, so a double tap cannot post twice.
  - Success and failure are announced through a live region rather than a page
    reload, because a grower part-way through a 20-line order must not lose it.
  - Grouping happens here, not in Liquid, because Liquid cannot build nested
    structures without fragile string juggling. Liquid decides the crop key; this
    file only buckets by it.
*/
(function () {
  'use strict';

  function money(cents, format) {
    var value = (Number(cents) / 100).toFixed(2);
    if (format && format.indexOf('{{amount}}') !== -1) {
      return format.replace('{{amount}}', value);
    }
    return '£' + value;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function FastOrder(root) {
    this.root = root;
    this.moneyFormat = root.getAttribute('data-money-format') || '£{{amount}}';
    this.cartUrl = root.getAttribute('data-cart-add-url') || '/cart/add.js';
    this.pendingLabel = root.getAttribute('data-lot-pending') || 'Lot confirmed at dispatch';
    this.busy = false;

    this.crops = this.buildCrops(this.readJson('[data-fastorder-data]'));
    this.usual = this.readJson('[data-fastorder-usual]') || {};

    this.state = {};
    this.filter = 'all';
    this.availableOnly = false;
    this.search = '';

    this.tbody = root.querySelector('[data-fastorder-tbody]');
    this.cards = root.querySelector('[data-fastorder-cards]');
    this.status = root.querySelector('[data-fastorder-status]');
    this.count = root.querySelector('[data-fastorder-count]');
    this.stickybar = root.querySelector('[data-fastorder-stickybar]');
    this.stickycount = root.querySelector('[data-fastorder-stickycount]');
    this.empty = root.querySelector('[data-fastorder-empty]');

    this.initState();
    this.bindControls();
    this.bindQuickAdd();
    this.render();
  }

  FastOrder.prototype.readJson = function (selector) {
    var node = this.root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  };

  /* ---------------------------------------------------------------
     Grouping — Liquid supplies one entry per product with a crop key.
     A crop becomes one row carrying one or more lines.
     --------------------------------------------------------------- */

  FastOrder.prototype.buildCrops = function (products) {
    if (!products || !products.length) return [];

    var byKey = {};
    var order = [];

    products.forEach(function (product) {
      if (!product.variants || !product.variants.length) return;

      if (!byKey[product.key]) {
        byKey[product.key] = { key: product.key, crop: product.crop, lines: [] };
        order.push(product.key);
      }

      byKey[product.key].lines.push({
        label: product.organic ? 'Organic' : 'Conventional',
        organic: !!product.organic,
        variety: product.variety || '',
        title: product.title,
        url: product.url,
        variants: product.variants
      });
    });

    return order
      .map(function (key) {
        var crop = byKey[key];
        // Organic first, so the certified line is the default where both exist.
        crop.lines.sort(function (a, b) {
          return (b.organic ? 1 : 0) - (a.organic ? 1 : 0);
        });
        // Two lines can share a certification status (three organic radish
        // varieties, say), so the variety joins the label — no two choices
        // in the dropdown may read identically.
        if (crop.lines.length > 1) {
          crop.lines.forEach(function (line) {
            if (line.variety) line.label = line.label + ' — ' + line.variety;
          });
        }
        return crop;
      })
      .sort(function (a, b) {
        return a.crop.localeCompare(b.crop);
      });
  };

  /* ---------------------------------------------------------------
     State: one entry per crop, holding the chosen line and pack.
     --------------------------------------------------------------- */

  FastOrder.prototype.initState = function () {
    var self = this;

    this.crops.forEach(function (crop) {
      var lineIndex = 0;
      var variantId = null;
      var preselect = self.usual[crop.key];

      // Honour the customer's own most recent purchase where it is still buyable.
      if (preselect) {
        crop.lines.forEach(function (line, i) {
          line.variants.forEach(function (variant) {
            if (String(variant.id) === String(preselect) && variant.available) {
              lineIndex = i;
              variantId = String(variant.id);
            }
          });
        });
      }

      if (!variantId) {
        var line = crop.lines[lineIndex];
        var firstAvailable = null;
        line.variants.forEach(function (variant) {
          if (!firstAvailable && variant.available) firstAvailable = variant;
        });
        variantId = String((firstAvailable || line.variants[0] || {}).id || '');
      }

      self.state[crop.key] = { line: lineIndex, variant: variantId, qty: 0 };
    });
  };

  FastOrder.prototype.currentLine = function (crop) {
    return crop.lines[this.state[crop.key].line] || crop.lines[0];
  };

  FastOrder.prototype.currentVariant = function (crop) {
    var st = this.state[crop.key];
    var line = this.currentLine(crop);
    if (!line) return null;
    for (var i = 0; i < line.variants.length; i++) {
      if (String(line.variants[i].id) === String(st.variant)) return line.variants[i];
    }
    return line.variants[0] || null;
  };

  /* ---------------------------------------------------------------
     Filtering
     --------------------------------------------------------------- */

  FastOrder.prototype.visibleCrops = function () {
    var self = this;
    var term = this.search.trim().toLowerCase();

    return this.crops.filter(function (crop) {
      if (term && crop.crop.toLowerCase().indexOf(term) === -1) return false;

      var matchesFilter = crop.lines.some(function (line) {
        if (self.filter === 'organic') return line.organic;
        if (self.filter === 'conventional') return !line.organic;
        return true;
      });
      if (!matchesFilter) return false;

      if (self.availableOnly) {
        var anyAvailable = crop.lines.some(function (line) {
          return line.variants.some(function (v) {
            return v.available;
          });
        });
        if (!anyAvailable) return false;
      }

      return true;
    });
  };

  /* ---------------------------------------------------------------
     Rendering
     --------------------------------------------------------------- */

  FastOrder.prototype.render = function () {
    var crops = this.visibleCrops();

    if (this.tbody) {
      this.tbody.innerHTML = '';
      crops.forEach(this.renderRow.bind(this));
    }
    if (this.cards) {
      this.cards.innerHTML = '';
      crops.forEach(this.renderCard.bind(this));
    }
    if (this.count) {
      this.count.textContent = crops.length + (crops.length === 1 ? ' crop' : ' crops') + ' shown';
    }
    if (this.empty) {
      this.empty.hidden = crops.length !== 0;
    }
    this.updateSelectionCount();
  };

  FastOrder.prototype.lineSelect = function (crop, idPrefix) {
    var self = this;
    var st = this.state[crop.key];

    // Rows are one seed line each, so most crops have nothing to choose here.
    // A dropdown holding a single option looks like a control that is broken;
    // the certification reads as a plain label instead.
    if (crop.lines.length < 2) {
      var only = el('span', 'gsg-fastorder__linestatic', crop.lines[0] ? crop.lines[0].label : '');
      only.id = idPrefix + '-line-' + crop.key;
      return only;
    }

    var select = el('select', 'gsg-fastorder__select');
    select.id = idPrefix + '-line-' + crop.key;

    crop.lines.forEach(function (line, i) {
      var opt = el('option', null, line.label);
      opt.value = String(i);
      if (i === st.line) opt.selected = true;
      select.appendChild(opt);
    });

    // A single-line crop still gets a select, but there is nothing to choose.
    select.disabled = crop.lines.length < 2;

    select.addEventListener('change', function () {
      st.line = parseInt(select.value, 10) || 0;
      var line = crop.lines[st.line];
      var firstAvailable = null;
      line.variants.forEach(function (v) {
        if (!firstAvailable && v.available) firstAvailable = v;
      });
      st.variant = String((firstAvailable || line.variants[0] || {}).id || '');
      self.render();
    });

    return select;
  };

  FastOrder.prototype.packSelect = function (crop, idPrefix) {
    var self = this;
    var st = this.state[crop.key];
    var line = this.currentLine(crop);
    var select = el('select', 'gsg-fastorder__select');
    select.id = idPrefix + '-pack-' + crop.key;

    line.variants.forEach(function (variant) {
      var opt = el('option', null, variant.title + (variant.available ? '' : ' — unavailable'));
      opt.value = String(variant.id);
      opt.disabled = !variant.available;
      if (String(variant.id) === String(st.variant)) opt.selected = true;
      select.appendChild(opt);
    });

    select.addEventListener('change', function () {
      st.variant = select.value;
      self.render();
    });

    return select;
  };

  FastOrder.prototype.qtyInput = function (crop, idPrefix) {
    var self = this;
    var st = this.state[crop.key];
    var variant = this.currentVariant(crop);
    var input = el('input', 'gsg-fastorder__qty');
    input.type = 'number';
    input.min = '0';
    input.step = '1';
    input.inputMode = 'numeric';
    input.value = st.qty ? String(st.qty) : '';
    input.id = idPrefix + '-qty-' + crop.key;
    input.disabled = !variant || !variant.available;

    input.addEventListener('input', function () {
      var n = parseInt(input.value, 10);
      st.qty = isNaN(n) || n < 0 ? 0 : n;
      self.updateSelectionCount();
    });

    return input;
  };

  FastOrder.prototype.lotText = function (variant) {
    if (variant && variant.lot) {
      return variant.lot + (variant.lotStatus ? ' · ' + variant.lotStatus : '');
    }
    return this.pendingLabel;
  };

  FastOrder.prototype.renderRow = function (crop) {
    var self = this;
    var variant = this.currentVariant(crop);
    var line = this.currentLine(crop);
    var available = !!(variant && variant.available);
    var tr = el('tr', available ? '' : 'gsg-fastorder__row--out');

    var th = el('th');
    th.scope = 'row';
    th.appendChild(el('span', 'gsg-fastorder__crop', crop.crop));
    if (line.variety) {
      th.appendChild(el('span', 'gsg-fastorder__variety', line.variety));
    }
    tr.appendChild(th);

    var lineCell = el('td');
    var lineSel = this.lineSelect(crop, 'd');
    lineSel.setAttribute('aria-label', 'Seed line for ' + crop.crop);
    lineCell.appendChild(lineSel);
    tr.appendChild(lineCell);

    var packCell = el('td');
    var packSel = this.packSelect(crop, 'd');
    packSel.setAttribute('aria-label', 'Pack size for ' + crop.crop);
    packCell.appendChild(packSel);
    tr.appendChild(packCell);

    var lotCell = el('td');
    lotCell.appendChild(
      el(
        'span',
        'gsg-fastorder__lot' + (variant && variant.lot ? '' : ' gsg-fastorder__lot--pending'),
        this.lotText(variant)
      )
    );
    tr.appendChild(lotCell);

    var priceCell = el('td', 'gsg-fastorder__price');
    if (available) {
      priceCell.textContent = money(variant.price, this.moneyFormat);
    } else {
      priceCell.appendChild(el('span', 'gsg-fastorder__oos', 'Unavailable'));
    }
    tr.appendChild(priceCell);

    var qtyCell = el('td');
    var qty = this.qtyInput(crop, 'd');
    qty.setAttribute('aria-label', 'Quantity for ' + crop.crop);
    qtyCell.appendChild(qty);
    tr.appendChild(qtyCell);

    var addCell = el('td');
    var addBtn = el('button', 'gsg-trade__btn gsg-trade__btn--secondary', 'Add');
    addBtn.type = 'button';
    addBtn.disabled = !available;
    addBtn.setAttribute('aria-label', 'Add ' + crop.crop + ' to basket');
    addBtn.addEventListener('click', function () {
      self.addSingle(crop, qty);
    });
    addCell.appendChild(addBtn);
    tr.appendChild(addCell);

    this.tbody.appendChild(tr);
  };

  FastOrder.prototype.renderCard = function (crop) {
    var self = this;
    var variant = this.currentVariant(crop);
    var line = this.currentLine(crop);
    var available = !!(variant && variant.available);

    var card = el('li', 'gsg-fastorder__card');

    var head = el('div', 'gsg-fastorder__card-head');
    var titleWrap = el('div');
    titleWrap.appendChild(el('h3', 'gsg-fastorder__card-title', crop.crop));
    if (line.variety) {
      titleWrap.appendChild(el('span', 'gsg-fastorder__variety', line.variety));
    }
    head.appendChild(titleWrap);
    if (!available) head.appendChild(el('span', 'gsg-fastorder__oos', 'Unavailable'));
    card.appendChild(head);

    var selects = el('div', 'gsg-fastorder__card-selects');

    var lineWrap = el('div', 'gsg-fastorder__field');
    var lineSel = this.lineSelect(crop, 'm');
    var lineLabel = el('label', 'gsg-fastorder__label', 'Line');
    // Only a real form control can be the target of a label.
    if (lineSel.tagName === 'SELECT') lineLabel.setAttribute('for', lineSel.id);
    lineWrap.appendChild(lineLabel);
    lineWrap.appendChild(lineSel);
    selects.appendChild(lineWrap);

    var packWrap = el('div', 'gsg-fastorder__field');
    var packSel = this.packSelect(crop, 'm');
    var packLabel = el('label', 'gsg-fastorder__label', 'Pack size');
    packLabel.setAttribute('for', packSel.id);
    packWrap.appendChild(packLabel);
    packWrap.appendChild(packSel);
    selects.appendChild(packWrap);

    card.appendChild(selects);

    var meta = el('div', 'gsg-fastorder__card-meta');
    meta.appendChild(
      el('span', 'gsg-fastorder__price', available ? money(variant.price, this.moneyFormat) : '—')
    );
    meta.appendChild(el('span', 'gsg-fastorder__lot', this.lotText(variant)));
    card.appendChild(meta);

    var foot = el('div', 'gsg-fastorder__card-foot');
    var qty = this.qtyInput(crop, 'm');
    var qtyLabel = el('label', 'visually-hidden', 'Quantity for ' + crop.crop);
    qtyLabel.setAttribute('for', qty.id);
    foot.appendChild(qtyLabel);
    foot.appendChild(qty);

    var addBtn = el('button', 'gsg-trade__btn gsg-trade__btn--primary', 'Add');
    addBtn.type = 'button';
    addBtn.disabled = !available;
    addBtn.setAttribute('aria-label', 'Add ' + crop.crop + ' to basket');
    addBtn.addEventListener('click', function () {
      self.addSingle(crop, qty);
    });
    foot.appendChild(addBtn);

    card.appendChild(foot);
    this.cards.appendChild(card);
  };

  /* ---------------------------------------------------------------
     Selection count and sticky bar
     --------------------------------------------------------------- */

  FastOrder.prototype.readyRows = function () {
    var self = this;
    return this.crops.filter(function (crop) {
      var st = self.state[crop.key];
      var variant = self.currentVariant(crop);
      return st.qty > 0 && variant && variant.available;
    });
  };

  FastOrder.prototype.updateSelectionCount = function () {
    var n = this.readyRows().length;

    if (this.stickycount) {
      this.stickycount.textContent = n + (n === 1 ? ' item ready' : ' items ready');
    }
    if (this.stickybar) {
      this.stickybar.setAttribute('data-visible', n > 0 ? 'true' : 'false');
    }

    var bulk = this.root.querySelectorAll('[data-fastorder-addselected]');
    for (var i = 0; i < bulk.length; i++) {
      bulk[i].disabled = n === 0;
    }
  };

  /* ---------------------------------------------------------------
     Cart writes
     --------------------------------------------------------------- */

  FastOrder.prototype.announce = function (message, state) {
    if (!this.status) return;
    this.status.setAttribute('data-state', state);
    this.status.textContent = message;
  };

  FastOrder.prototype.addSingle = function (crop, qtyInput) {
    var st = this.state[crop.key];
    var variant = this.currentVariant(crop);
    if (!variant || !variant.available) return;

    var qty = st.qty > 0 ? st.qty : 1;
    var self = this;

    this.postItems([{ id: variant.id, quantity: qty }], function () {
      st.qty = 0;
      if (qtyInput) qtyInput.value = '';
      self.updateSelectionCount();
      return qty + ' × ' + crop.crop + ' ' + variant.title + ' added to your basket.';
    });
  };

  FastOrder.prototype.addSelected = function () {
    var self = this;
    var ready = this.readyRows();
    if (!ready.length) return;

    var items = ready.map(function (crop) {
      return { id: self.currentVariant(crop).id, quantity: self.state[crop.key].qty };
    });

    this.postItems(items, function () {
      ready.forEach(function (crop) {
        self.state[crop.key].qty = 0;
      });
      self.render();
      return items.length + (items.length === 1 ? ' line' : ' lines') + ' added to your basket.';
    });
  };

  FastOrder.prototype.postItems = function (items, onSuccess) {
    var self = this;
    if (this.busy) return;
    this.busy = true;
    this.announce('Adding to basket…', 'busy');

    fetch(this.cartUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) {
            throw new Error(
              body && body.description ? body.description : 'Could not add to basket.'
            );
          }
          return body;
        });
      })
      .then(function () {
        var message = onSuccess ? onSuccess() : 'Added to your basket.';
        self.announce(message, 'success');
        self.refreshCartCount();
      })
      .catch(function (error) {
        self.announce(error.message || 'Could not add to basket. Please try again.', 'error');
      })
      .then(function () {
        self.busy = false;
      });
  };

  // Keep the header bubble honest without reloading the page.
  FastOrder.prototype.refreshCartCount = function () {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
        for (var i = 0; i < bubbles.length; i++) {
          bubbles[i].textContent = cart.item_count;
        }
      })
      .catch(function () {
        /* the basket page remains the source of truth */
      });
  };

  /* ---------------------------------------------------------------
     Controls
     --------------------------------------------------------------- */

  FastOrder.prototype.bindControls = function () {
    var self = this;

    var search = this.root.querySelector('[data-fastorder-search]');
    if (search) {
      search.addEventListener('input', function () {
        self.search = search.value;
        self.render();
      });
    }

    var filters = this.root.querySelectorAll('[data-fastorder-filter]');
    for (var i = 0; i < filters.length; i++) {
      filters[i].addEventListener('change', function (event) {
        self.filter = event.target.value;
        self.render();
      });
    }

    var availableOnly = this.root.querySelector('[data-fastorder-available]');
    if (availableOnly) {
      availableOnly.addEventListener('change', function () {
        self.availableOnly = availableOnly.checked;
        self.render();
      });
    }

    var bulk = this.root.querySelectorAll('[data-fastorder-addselected]');
    for (var b = 0; b < bulk.length; b++) {
      bulk[b].addEventListener('click', function () {
        self.addSelected();
      });
    }
  };

  // "Your usual seeds" posts straight to the basket — no shared row state, so a
  // quick add can never collide with a part-filled catalogue row.
  FastOrder.prototype.bindQuickAdd = function () {
    var self = this;
    var buttons = this.root.querySelectorAll('[data-fastorder-quickadd]');

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function (event) {
        var button = event.currentTarget;
        var id = button.getAttribute('data-variant-id');
        var label = button.getAttribute('data-label') || 'Item';
        if (!id) return;
        self.postItems([{ id: Number(id), quantity: 1 }], function () {
          return '1 × ' + label + ' added to your basket.';
        });
      });
    }
  };

  function init() {
    var roots = document.querySelectorAll('[data-fastorder]');
    for (var i = 0; i < roots.length; i++) {
      new FastOrder(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
